import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { roleRoutes } from "utils/roles";
import { useAuthToken } from "../../providers/authProviders";
import useTenantModules from "../../hooks/useTenantModules";

/**
 * Deja pasar a una herramienta que la plataforma habilita por empresa (módulo
 * de `users/tenant-modules`, p. ej. el Rutificador), y solo a los roles que la
 * usan. Es la contraparte de ruta de la curaduría del menú (menuConfig): sin
 * esto, la URL directa abriría la pantalla a quien no tiene el módulo.
 *
 * Falla CERRADO: mientras no hay respuesta muestra `loadingFallback`, y si la
 * consulta falla no renderiza la herramienta sino un aviso con "Reintentar".
 * Abrirla "por las dudas" ante un error de red sería dar acceso por accidente.
 *
 * El rol se lee del token (useAuthToken, síncrono), igual que
 * RequireSystemSuperadmin; los módulos, de useTenantModules.
 *
 * Los módulos solo valen si son del tipo de empresa del portal (`tenantType`).
 * El backend ya elige el tenant según el rol (a un rol de comercio le responde
 * siempre los de su comercio), pero acá se vuelve a exigir el tipo como defensa
 * en profundidad: si la respuesta fuera de otra empresa (un backend viejo, un
 * rol nuevo sin clasificar), abrir la herramienta sería habilitarla con el
 * permiso de otra empresa.
 *
 * @param {object} props
 * @param {string} props.moduleKey - Clave del catálogo de módulos ("rutificador").
 * @param {'logistic'|'company'} props.tenantType - Tipo de empresa del portal
 *   ('logistic' en /admin, 'company' en /home). Sin él la herramienta no se
 *   abre para nadie salvo el superadmin del sistema con `allowSystemSuperadmin`.
 * @param {string[]} props.allowedRoles - _id de los roles que usan la herramienta.
 * @param {boolean} [props.allowSystemSuperadmin=false] - El superadmin del sistema
 *   entra siempre, sin consultar módulos.
 * @param {string} props.fallbackPath - A dónde se redirige (replace) a un rol no
 *   admitido o a una empresa sin el módulo.
 * @param {React.ReactNode} [props.loadingFallback=null] - Lo que se ve mientras
 *   se consultan los módulos.
 * @param {React.ReactNode} props.children
 */
export default function RequireTenantModule({
  moduleKey,
  tenantType,
  allowedRoles = [],
  allowSystemSuperadmin = false,
  fallbackPath,
  loadingFallback = null,
  children,
}) {
  const { t } = useTranslation();
  const { isAuthenticated, role } = useAuthToken();
  const { status, has, retry, tenantType: modulesTenantType } = useTenantModules();

  if (!isAuthenticated) {
    return <Navigate to="/" />;
  }
  if (allowSystemSuperadmin && role === roleRoutes.systemSuperadmin) {
    return children;
  }
  if (!allowedRoles.includes(role)) {
    return <Navigate to={fallbackPath} replace />;
  }
  if (status === "loading") {
    return loadingFallback;
  }
  if (status === "error") {
    return (
      <div
        className="m-4 p-4 rounded-lg border border-border bg-bg-muted/40 text-sm text-fg-muted"
        role="alert"
      >
        <span className="inline-flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-status-warning shrink-0" aria-hidden="true" />
          {t("No pudimos verificar el acceso a esta herramienta.")}
        </span>
        <button
          type="button"
          onClick={retry}
          className="ml-3 inline-flex items-center gap-1.5 text-brand-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
        >
          <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
          {t("Reintentar")}
        </button>
      </div>
    );
  }
  if (modulesTenantType !== tenantType || !has(moduleKey)) {
    return <Navigate to={fallbackPath} replace />;
  }

  return children;
}
